import { FC } from "react";
import {
    Grid,
    Box,
    useBreakpointValue,
    Flex,
    Heading,
    GridItem,
    Picture,
} from "@smile2impress/components/relaunch";
import { WavesPatternIcon } from "@smile2impress/components";

import { RichText } from "components/RichText";
import { CtaButton } from "components/CtaButton";

import { Advantage } from "./components";
import { VerticalAdvantagesProps } from "./VerticalAdvantages.types";

export const VerticalAdvantages: FC<VerticalAdvantagesProps> = ({
    content,
    headingAs,
}) => {
    const isMobile = useBreakpointValue({
        base: true,
        tablet: true,
        desktop: false,
    });
    const { title, subtitle, cta, advantages, image, applyCampaignTheme } =
        content;

    return (
        <Grid
            gridTemplateColumns={["1fr", "1fr", "repeat(24, 1fr)"]}
            gridColumnGap={["initial", "initial", 2]}
        >
            <GridItem
                colStart={["auto", "auto", 2]}
                colSpan={["auto", "auto", 10]}
            >
                {!!title && (
                    <Box h="100%" px={[2, 2, 0]} pb={[3, 3, 0]}>
                        <Heading
                            as={headingAs}
                            textStyle="title1"
                            color="text.base"
                        >
                            {title}
                        </Heading>
                        {!!subtitle && (
                            <RichText
                                content={subtitle}
                                textStyle="body"
                                color="text.base"
                                mt={[0.7, 0.7, 1]}
                            />
                        )}
                        {!isMobile && cta && (
                            <Box mt={2}>
                                <CtaButton {...cta} />
                            </Box>
                        )}
                    </Box>
                )}
            </GridItem>
            <GridItem
                colStart={["auto", "auto", 13]}
                colSpan={["auto", "auto", 11]}
            >
                <Box
                    as="section"
                    position="relative"
                    {...(advantages && {
                        bgColor: "base.blue",
                        borderRadius: ["initial", "initial", "sm"],
                        overflow: "hidden",
                    })}
                    {...(applyCampaignTheme && {
                        bgColor: "base.gunmetal",
                    })}
                >
                    {!image && (
                        <Box
                            position="absolute"
                            top={["-160px", "-160px", "-330px"]}
                            left={["-140px", "-140px", "-270px"]}
                            transform="rotate(360deg)"
                            zIndex="0"
                            {...(applyCampaignTheme && {
                                filter: "brightness(0.1) contrast(1.2)",
                            })}
                        >
                            {isMobile && (
                                <WavesPatternIcon
                                    width="598px"
                                    height="640px"
                                    gradientColors={[
                                        "hsla(230, 76%, 51%, 1)",
                                        "hsla(274, 76%, 51%, 1)",
                                        "hsla(359, 100%, 66%, 1)",
                                    ]}
                                />
                            )}
                            {!isMobile && (
                                <WavesPatternIcon
                                    width="1140px"
                                    height="1217px"
                                    gradientColors={[
                                        "hsla(230, 76%, 51%, 1)",
                                        "hsla(274, 76%, 51%, 1)",
                                        "hsla(359, 100%, 66%, 1)",
                                    ]}
                                />
                            )}
                        </Box>
                    )}
                    <Flex
                        px={image ? [0] : [2, 2, 4]}
                        pt={image ? [0] : [3, 3, 4]}
                        pb={[3, 3, 4]}
                    >
                        <Flex
                            position="relative"
                            direction="column"
                            flexWrap={["wrap", "wrap", "nowrap"]}
                            rowGap={[2, 2, 3]}
                            w="100%"
                            zIndex="1"
                        >
                            {advantages &&
                                advantages?.length > 0 &&
                                advantages.map((item, index) => (
                                    <Flex key={index}>
                                        <Advantage {...item} />
                                    </Flex>
                                ))}
                            {image && (
                                <Box
                                    borderRadius={["initial", "initial", "sm"]}
                                    overflow="hidden"
                                >
                                    <Picture {...image} />
                                </Box>
                            )}
                            {isMobile && cta && (
                                <GridItem
                                    colStart={["auto", "auto", 15]}
                                    colSpan={["auto", "auto", 4]}
                                    px={image ? [2, 0, 0] : [0]}
                                >
                                    <CtaButton
                                        variant={image ? "stroked" : "white"}
                                        {...cta}
                                    />
                                </GridItem>
                            )}
                        </Flex>
                    </Flex>
                </Box>
            </GridItem>
        </Grid>
    );
};
